import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { MapPin, ArrowLeft, Building2, Gift, Calendar, Star, Loader2, BookOpen } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import ConsumerLayout from '@/components/ConsumerLayout';
import { venueUrl, experienceUrl } from '@/lib/urls';
import api from '@/lib/api';

const ICON_MAP = { beer: '🍺', music: '🎵', moon: '🌙', sun: '☀️', star: '⭐', heart: '❤️', utensils: '🍽️', palette: '🎨', glass: '🍸', book: '📚', building: '🏢' };

export default function AutoGuidePage() {
  const { guideSlug, state, city, neighborhood } = useParams();
  const [guide, setGuide] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const location = [state, city, neighborhood].filter(Boolean).join('/');
    api.get(`/guides/auto/${guideSlug}${location ? `?location=${location}` : ''}`)
      .then(res => setGuide(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [guideSlug, state, city, neighborhood]);

  if (loading) return <ConsumerLayout darkTheme><div className="flex items-center justify-center h-96"><Loader2 className="w-8 h-8 animate-spin text-indigo-600" /></div></ConsumerLayout>;
  if (!guide) return <ConsumerLayout darkTheme><div className="text-center py-20 text-slate-400">Guide not found</div></ConsumerLayout>;

  return (
    <ConsumerLayout darkTheme>
      <div className="max-w-4xl mx-auto px-4 py-12">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-sm text-slate-400 mb-6">
          <Link to="/guides" className="hover:text-white">Guides</Link>
          {guide.state && <><span>/</span><Link to={`/guides/${guide.state?.toLowerCase()}`} className="hover:text-white">{guide.state}</Link></>}
          {guide.city && <><span>/</span><Link to={`/guides/${guide.state?.toLowerCase()}/${guide.city?.toLowerCase()}`} className="hover:text-white">{guide.city}</Link></>}
        </div>

        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-3">
            <span className="text-3xl">{ICON_MAP[guide.icon] || '📍'}</span>
            <Badge className="bg-indigo-600/20 text-indigo-300 border-indigo-500/30">{guide.category}</Badge>
            {guide.neighborhood && <Badge variant="outline" className="text-slate-400">{guide.neighborhood}</Badge>}
          </div>
          <h1 className="text-3xl lg:text-4xl font-bold text-white mb-3">{guide.title}</h1>
          <p className="text-lg text-slate-400">{guide.description}</p>
        </div>

        {/* AI-generated intro */}
        {guide.intro && (
          <div className="prose prose-invert max-w-none mb-10 p-6 bg-slate-800/50 rounded-xl border border-slate-700">
            <p className="text-slate-300 leading-relaxed text-lg">{guide.intro}</p>
          </div>
        )}

        {/* Picks */}
        <div className="space-y-6">
          <h2 className="text-xl font-bold text-white flex items-center gap-2"><BookOpen className="w-5 h-5 text-indigo-400" /> Our Picks ({guide.item_count || guide.items?.length || 0})</h2>
          
          {(guide.picks || []).map((pick, i) => {
            const item = (guide.items || []).find(it => 
              it.name === pick.name || it.title === pick.name
            ) || (guide.items || [])[i];
            
            const isVenue = pick.type === 'venue' || item?._type === 'venue';
            const isExperience = pick.type === 'experience' || item?._type === 'experience';
            const linkTo = isVenue && item ? venueUrl(item) : isExperience && item ? experienceUrl(item) : item ? `/events/${item.id}` : '#';
            
            return (
              <Link key={i} to={linkTo}>
                <Card className="bg-slate-800/50 border-slate-700 hover:border-indigo-500 transition-all overflow-hidden">
                  <CardContent className="p-0">
                    <div className="flex">
                      {/* Number */}
                      <div className="w-16 flex-shrink-0 bg-gradient-to-b from-indigo-600 to-purple-600 flex items-center justify-center">
                        <span className="text-2xl font-bold text-white">{i + 1}</span>
                      </div>
                      {/* Image */}
                      {item?.images?.[0] && (
                        <div className="w-32 flex-shrink-0"><img src={item.images[0]} alt="" className="w-full h-full object-cover" /></div>
                      )}
                      {/* Content */}
                      <div className="p-4 flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <Badge variant="secondary" className="text-[10px]">{pick.type || item?._type || 'venue'}</Badge>
                          {isExperience && item?.price && <span className="text-sm text-purple-400 font-bold">${item.price}</span>}
                        </div>
                        <h3 className="font-bold text-white text-lg">{pick.name}</h3>
                        <p className="text-sm text-slate-400 mt-1">{pick.recommendation}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            );
          })}
        </div>

        {/* Auto-generated notice */}
        <p className="text-xs text-slate-600 text-center mt-10">
          This guide is auto-generated from EVENT★GOERS listings and updated weekly.
        </p>
      </div>
    </ConsumerLayout>
  );
}
